import { useEffect, useState, useRef, useCallback } from 'react';
import LoadSimple from '../LoadSimple';
import { capitalizeFirstLetter } from '../../utils/utils';
import './styles/modalHistoric.css';
import { lang } from '../generales/traduction';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler, zoomPlugin
);

const formatValue = (v) => {
  const n = parseFloat(v);
  if (isNaN(n)) return v;
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

/* ─── crosshair plugin ─── */
const crosshairPlugin = {
  id: 'crosshairLine',
  afterDraw(chart) {
    if (chart.tooltip?._active?.length) {
      const ctx = chart.ctx;
      const x = chart.tooltip._active[0].element.x;
      const topY = chart.scales.y.top;
      const bottomY = chart.scales.y.bottom;
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, topY);
      ctx.lineTo(x, bottomY);
      ctx.lineWidth = 1;
      ctx.strokeStyle = 'rgba(66,208,200,0.35)';
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.restore();
    }
  },
};
ChartJS.register(crosshairPlugin);

/* ════════════════════════════════════════════════════════════════ */

const ModalHistoric = ({
  languaje, hostSelected, server, mode, handleShowPopup, closeAddHostModal,
}) => {
  const chartRef = useRef(null);
  const [metric, setMetric] = useState({});
  const [yAxisAuto, setYAxisAuto] = useState(false);
  const timeRange = 48;
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);

  const filteredMetrics = hostSelected.metrics.filter((m) =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [dataHistoric, setDataHistoric] = useState({ data: {}, loading: false, error: {} });

  const select_metric = (element) => setMetric(element);

  useEffect(() => {
    if (Object.values(metric).length !== 0) search_historic();
  }, [metric]);

  function getCurrentAndPastTime() {
    const now = new Date();
    const past = new Date(now.getTime() - timeRange * 60 * 60 * 1000);
    return { current: now.toISOString(), past: past.toISOString() };
  }

  function search_historic() {
    setDataHistoric({ data: {}, loading: true, error: dataHistoric.error });
    const { current, past } = getCurrentAndPastTime();
    const data = {
      hostid: hostSelected.hostid,
      item_key: metric.item_key,
      start_time: past.replace('T', ' ').slice(0, -8),
      end_time: current.replace('T', ' ').slice(0, -8),
    };
    const fetchData = async () => {
      try {
        const response = await fetch(
          server.url + '/api/v1/cassia/metric_reports/data/historical',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              accept: 'application/json',
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
            body: JSON.stringify(data),
          }
        );
        if (response.ok) {
          const response_data = await response.json();
          const newDataset = {
            label: metric.name,
            data: response_data.data.map((obj) => obj.value),
            borderColor: '#42D0C8',
            backgroundColor: (ctx) => {
              const chart = ctx.chart;
              const { ctx: c, chartArea } = chart;
              if (!chartArea) return 'rgba(66,208,200,0.1)';
              const gradient = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, 'rgba(66,208,200,0.30)');
              gradient.addColorStop(1, 'rgba(66,208,200,0.02)');
              return gradient;
            },
            fill: true,
            tension: 0.35,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: '#42D0C8',
            pointHoverBorderWidth: 2.5,
            borderWidth: 2.5,
          };
          setChartData({
            labels: response_data.data.map((obj) => obj.timestamp.slice(0, -3)),
            datasets: [newDataset],
          });
          setDataHistoric({ data: response_data.data, loading: false, error: dataHistoric.error });
        } else {
          setDataHistoric({ data: [], loading: false, error: dataHistoric.error });
        }
      } catch (error) { /* silent */ }
    };
    fetchData();
  }

  /* ── Y-axis config ── */
  const getYScaleConfig = useCallback(() => {
    if (!chartData.datasets.length || !chartData.datasets[0].data?.length) return {};
    const values = chartData.datasets[0].data.map(Number).filter((v) => !isNaN(v));
    if (!values.length) return {};
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    const pad = (dataMax - dataMin || 1) * 0.08;

    return {
      ...(yAxisAuto ? { min: dataMin - pad, max: dataMax + pad } : { beginAtZero: true }),
      grid: { color: 'rgba(200,200,200,0.12)', drawBorder: false },
      ticks: { color: '#94a3b8', font: { size: 10 }, padding: 8, maxTicksLimit: 7 },
    };
  }, [chartData, yAxisAuto]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500, easing: 'easeOutQuart' },
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: getYScaleConfig(),
      x: {
        grid: { color: 'rgba(200,200,200,0.08)', drawBorder: false },
        ticks: { color: '#94a3b8', font: { size: 9 }, maxRotation: 45, maxTicksLimit: 10 },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(15,23,42,0.92)',
        titleColor: '#cbd5e1',
        bodyColor: '#fff',
        titleFont: { size: 11 },
        bodyFont: { size: 13, weight: 'bold' },
        padding: 12,
        cornerRadius: 10,
        displayColors: false,
        callbacks: {
          label: (item) => {
            const v = parseFloat(item.raw);
            const formatted = isNaN(v) ? item.raw : formatValue(v);
            return `${formatted} ${metric.units || ''}`;
          },
        },
      },
      zoom: {
        pan: { enabled: true, mode: 'x' },
        zoom: { wheel: { enabled: true, modifierKey: 'ctrl' }, pinch: { enabled: true }, mode: 'x' },
      },
    },
  };

  const handleResetZoom = () => { if (chartRef.current) chartRef.current.resetZoom(); };

  // ─── StatusBadge helper ───
  const StatusBadge = ({ danger, value }) => {
    if (typeof value === 'string') {
      const isError = danger == 1;
      return (
        <span className={`mh-status-badge ${isError ? 'mh-status-error' : 'mh-status-success'}`}>
          {value}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="mh-container">
      {/* LEFT PANEL */}
      <div className="mh-left-panel">
        <div className="mh-metrics-header">
          {!isSearchActive && !searchQuery ? (
            <>
              <span className="mh-metrics-title">
                Métricas Monitorizadas ({filteredMetrics.length})
              </span>
              <button
                className="mh-search-toggle"
                onClick={() => setIsSearchActive(true)}
                title="Buscar métrica"
              >
                🔍
              </button>
            </>
          ) : (
            <div className="mh-search-input-wrap">
              <span className="mh-search-icon">🔍</span>
              <input
                autoFocus
                type="text"
                className="mh-search-input"
                placeholder="Buscar métrica..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={() => { if (!searchQuery) setIsSearchActive(false); }}
              />
              <button
                className="mh-search-clear"
                onClick={() => { setSearchQuery(''); setIsSearchActive(false); }}
                title="Cerrar búsqueda"
              >
                ✕
              </button>
            </div>
          )}
        </div>
        <div className="mh-metrics-list">
          {filteredMetrics.length > 0 ? (
            filteredMetrics.map((elemento, indice) => {
              const isSelected = metric.name === elemento.name;
              return (
                <button
                  key={indice}
                  className={`mh-metric-card ${isSelected ? 'mh-metric-selected' : ''}`}
                  onClick={() => select_metric(elemento)}
                >
                  <div className="mh-metric-info">
                    <span className={`mh-metric-name ${isSelected ? 'mh-metric-name-active' : ''}`}>
                      {elemento.name}
                    </span>
                    {elemento.last_update && (
                      <span className={`mh-metric-lastupdate ${isSelected ? 'mh-metric-lastupdate-active' : ''}`}>
                        ⏱ {elemento.last_update}
                      </span>
                    )}
                  </div>
                  <div className="mh-metric-value-area">
                    {isNaN(parseFloat(elemento.value)) ? (
                      <StatusBadge danger={elemento.danger} value={elemento.value} />
                    ) : (
                      <div className="mh-metric-numeric">
                        <span 
                          className={`mh-metric-val ${isSelected ? 'mh-metric-val-active' : ''}`}
                          style={{ color: elemento.danger == 1 ? '#e11d48' : '#059669' }}
                        >
                          {formatValue(elemento.value)}
                        </span>
                        {elemento.units && (
                          <span 
                            className={`mh-metric-unit ${isSelected ? 'mh-metric-unit-active' : ''}`}
                            style={{ color: elemento.danger == 1 ? 'rgba(225, 29, 72, 0.8)' : 'rgba(5, 150, 105, 0.8)' }}
                          >
                            {elemento.units}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          ) : (
            <div className="mh-search-empty">
              No se encontraron métricas para "<strong>{searchQuery}</strong>".
            </div>
          )}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="mh-right-panel">
        {Object.values(metric).length === 0 ? (
          <div className="mh-empty-state">
            {/* Animated background particles */}
            <div className="mh-empty-particles">
              <span className="mh-particle p1"></span>
              <span className="mh-particle p2"></span>
              <span className="mh-particle p3"></span>
              <span className="mh-particle p4"></span>
              <span className="mh-particle p5"></span>
            </div>

            {/* Animated chart SVG */}
            <div className="mh-empty-illustration">
              <svg viewBox="0 0 200 140" fill="none" className="mh-empty-svg">
                {/* Grid lines */}
                <line x1="30" y1="20" x2="30" y2="110" stroke="#e2e8f0" strokeWidth="1" />
                <line x1="30" y1="110" x2="185" y2="110" stroke="#e2e8f0" strokeWidth="1" />
                <line x1="30" y1="40" x2="185" y2="40" stroke="#f1f5f9" strokeWidth="0.5" strokeDasharray="4" />
                <line x1="30" y1="65" x2="185" y2="65" stroke="#f1f5f9" strokeWidth="0.5" strokeDasharray="4" />
                <line x1="30" y1="90" x2="185" y2="90" stroke="#f1f5f9" strokeWidth="0.5" strokeDasharray="4" />

                {/* Gradient fill under the line */}
                <defs>
                  <linearGradient id="emptyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0B2A5B" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="#0B2A5B" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M35,85 C55,80 65,50 85,42 C105,34 115,70 135,60 C155,50 165,30 180,25 L180,110 L35,110 Z"
                  fill="url(#emptyGrad)" className="mh-empty-area" />

                {/* Main animated line */}
                <path d="M35,85 C55,80 65,50 85,42 C105,34 115,70 135,60 C155,50 165,30 180,25"
                  stroke="#0B2A5B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  className="mh-empty-line" fill="none" />

                {/* Animated data points */}
                <circle cx="85" cy="42" r="4" fill="#fff" stroke="#0B2A5B" strokeWidth="2" className="mh-empty-dot d1" />
                <circle cx="135" cy="60" r="4" fill="#fff" stroke="#0B2A5B" strokeWidth="2" className="mh-empty-dot d2" />
                <circle cx="180" cy="25" r="4" fill="#fff" stroke="#10b981" strokeWidth="2" className="mh-empty-dot d3" />

                {/* Pulse ring on last point */}
                <circle cx="180" cy="25" r="8" fill="none" stroke="#10b981" strokeWidth="1" className="mh-empty-pulse-ring" opacity="0" />
              </svg>
            </div>

            {/* CTA content */}
            <div className="mh-empty-cta">
              <h3 className="mh-empty-title">Selecciona una métrica</h3>
              <p className="mh-empty-subtitle">
                Haz clic en cualquiera de las métricas del panel izquierdo para visualizar
                su historial detallado y comportamiento.
              </p>
              <div className="mh-empty-arrow">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Chart Header */}
            <div className="mh-chart-header">
              <div>
                <h3 className="mh-chart-title">Histórico de {metric.name}</h3>
              </div>
              <div className="mh-chart-controls">
                <div className="mh-yaxis-toggle">
                  <span className="mh-toggle-label">Eje Y:</span>
                  <button
                    className={`mh-toggle-btn ${yAxisAuto ? 'mh-toggle-active' : ''}`}
                    onClick={() => setYAxisAuto(true)}
                  >Auto</button>
                  <button
                    className={`mh-toggle-btn ${!yAxisAuto ? 'mh-toggle-active' : ''}`}
                    onClick={() => setYAxisAuto(false)}
                  >Desde 0</button>
                </div>
                <div className="mh-divider"></div>
                <span className="mh-time-badge">Últimas {timeRange} hrs</span>
                <button className="mh-refresh-btn" onClick={handleResetZoom} title="Reset zoom">
                  ↺
                </button>
              </div>
            </div>

            {/* Chart Area */}
            <div className="mh-chart-area">
              {dataHistoric.loading ? (
                <div className="mh-loading"><LoadSimple /></div>
              ) : dataHistoric.data.length === 0 ? (
                <div className="mh-no-results">{capitalizeFirstLetter(lang[languaje]['sinResultados'])}.</div>
              ) : (
                <>
                  <div className="mh-chart-hint">
                    <span>ℹ Scroll para zoom · Arrastrar para desplazar</span>
                  </div>
                  <Line ref={chartRef} data={chartData} options={chartOptions} />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ModalHistoric;